import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { dashboardAPI, ordersAPI } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { TrendingUp, TrendingDown, IndianRupee, ShoppingBag, Users, ChefHat, X, ChevronLeft, ChevronRight } from 'lucide-react';

const PERIODS = ['Last 7 days', 'Last 30 days', 'Last 3 months'];
const STATUS_OPTS = ['ALL', 'NEW', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'];

function maskOrderId(id) {
  if (!id) return '—';
  const s = String(id).replace(/^#/, '');
  if (s.length <= 6) return '#' + s;
  return '#' + s.slice(0, 3) + '*'.repeat(s.length - 6) + s.slice(-3);
}

function StatCard({ card, type }) {
  if (!card) return <div className={`stat-card ${type}`} style={{ minHeight: 130 }} />;
  const Icon = { revenue: IndianRupee, orders: ShoppingBag, sellers: ChefHat, customers: Users }[type];
  return (
    <div className={`stat-card ${type}`}>
      <div className={`stat-icon ${type}`}><Icon size={20} /></div>
      <div className="stat-value">{card.displayValue}</div>
      <div className="stat-label">{card.label}</div>
      <div className={`stat-change ${card.increased ? 'up' : 'down'}`}>
        {card.increased ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {card.changeLabel}
      </div>
    </div>
  );
}

StatCard.propTypes = {
  card: PropTypes.shape({
    displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    increased: PropTypes.bool,
    changeLabel: PropTypes.string,
  }),
  type: PropTypes.string.isRequired,
};

StatCard.defaultProps = {
  card: null,
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4 }}>{label}</p>
        <p style={{ color: 'var(--green)', fontSize: 14, fontWeight: 700 }}>₹{Number(payload[0].value).toLocaleString('en-IN')}</p>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number })),
  label: PropTypes.string,
};

CustomTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

function getBadgeClass(status) {
  if (status) return status.toLowerCase().replaceAll('_', '-');
  return 'new';
}

function OrderDetailModal({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersAPI.getById(orderId)
      .then(r => { setOrder(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [orderId]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <span className="modal-title">Order Details</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {loading ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : !order ? (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Could not load order</p>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--primary-dark)' }}>
                {maskOrderId(order.displayId || order.orderId)}
              </span>
              <span className={`badge ${getBadgeClass(order.status)}`}>
                {order.status?.replaceAll('_', ' ') || '—'}
              </span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Order Items</div>
              {(order.items || []).map((item, i) => (
                <div key={item.productId || item.productName || i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-primary)' }}>
                    {item.productName} <span style={{ color: 'var(--text-muted)' }}>× {item.quantity}</span>
                  </span>
                  <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                    ₹{Number((item.price ?? item.totalPrice ?? item.unitPrice ?? 0) * (item.quantity ?? 1)).toLocaleString('en-IN')}
                  </span>
                </div>
              ))}
            </div>
            {[
              ['Customer', order.customerName],
              ['Seller', order.sellerName || order.seller?.businessName || order.sellerBusinessName || '—'],
              ['Address', order.deliveryAddress],
              ['Payment', order.paymentMethod],
              ['Total Amount', `₹${Number(order.totalAmount).toLocaleString('en-IN')}`],
              ['Placed At', order.createdAt ? new Date(order.createdAt).toLocaleString('en-IN') : '—'],
            ].map(([k, v]) => (
              <div className="detail-row" key={k}>
                <span className="detail-label">{k}</span>
                <span className="detail-value" style={{ textAlign: 'right', maxWidth: '60%' }}>{v || '—'}</span>
              </div>
            ))}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

OrderDetailModal.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
};

function MobileOrderCard({ order, onClick }) {
  const statusColor = {
    DELIVERED: 'var(--green)',
    NEW: 'var(--yellow)',
    OUT_FOR_DELIVERY: 'var(--blue)',
    CANCELLED: 'var(--red)',
    READY: 'var(--orange)',
  }[order.status] || 'var(--border)';

  return (
    <button
      type="button"
      onClick={() => onClick(order.orderId)}
      style={{
        background: order.status === 'READY' ? '#fff7ed' : 'var(--bg-card)',
        border: `1px solid ${order.status === 'READY' ? '#fed7aa' : 'var(--border)'}`,
        borderRadius: 14,
        padding: '14px 14px 14px 18px',
        marginBottom: 10,
        cursor: 'pointer',
        boxShadow: 'var(--shadow-xs)',
        WebkitTapHighlightColor: 'transparent',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.15s',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        background: statusColor,
        borderRadius: '0 3px 3px 0',
      }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--primary-dark)', fontWeight: 700 }}>
            {maskOrderId(order.displayId || order.orderId)}
          </span>
          {order.status === 'READY' && (
            <span style={{ fontSize: 9, background: '#f97316', color: '#fff', borderRadius: 4, padding: '2px 5px', fontWeight: 700 }}>
              READY
            </span>
          )}
        </div>
        <span className={`badge ${getBadgeClass(order.status)}`} style={{ fontSize: 10, padding: '3px 8px' }}>
          {order.status?.replaceAll('_', ' ') || '—'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Customer</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {order.customerName || '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Seller</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {order.sellerName || '—'}
          </div>
        </div>
      </div>
      {order.productNames && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Items</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {order.productNames}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--green)', letterSpacing: -0.5 }}>
          ₹{Number(order.amount).toLocaleString('en-IN')}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {order.createdAt
            ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })
            : '—'}
        </span>
      </div>
    </button>
  );
}

MobileOrderCard.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    customerName: PropTypes.string,
    sellerName: PropTypes.string,
    productNames: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createdAt: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('Last 7 days');
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersTotalPages, setOrdersTotalPages] = useState(0);
  const [ordersTotal, setOrdersTotal] = useState(0);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    setLoading(true);
    dashboardAPI.get(period)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    setOrdersLoading(true);
    const statusParam = statusFilter === 'ALL' ? undefined : statusFilter;
    ordersAPI.getAll(statusParam, ordersPage, 10)
      .then(r => {
        setRecentOrders(r.data?.content || []);
        setOrdersTotalPages(r.data?.totalPages || 0);
        setOrdersTotal(r.data?.totalElements || 0);
      })
      .catch(() => {})
      .finally(() => setOrdersLoading(false));
  }, [statusFilter, ordersPage]);

  useEffect(() => { setOrdersPage(0); }, [statusFilter]);

  const ordersByStatus = data?.ordersByStatus;

  return (
    <Layout title="Dashboard" subtitle="Welcome back, Admin">

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <div className="tabs" style={{ width: 'fit-content' }}>
          {PERIODS.map(p => (
            <button key={p} className={`tab ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="stat-grid">
        <StatCard card={data?.totalRevenue} type="revenue" />
        <StatCard card={data?.activeOrders} type="orders" />
        <StatCard card={data?.activeSellers} type="sellers" />
        <StatCard card={data?.totalCustomers} type="customers" />
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Revenue Overview</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{period}</span>
          </div>
          {loading ? (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="spinner" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data?.revenueChart || []} barSize={28}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="dayLabel" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(108,99,255,0.06)' }} />
                <Bar dataKey="revenue" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#32CD32" />
                    <stop offset="100%" stopColor="#28a428" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Orders by Status</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ordersByStatus?.total || 0} total</span>
          </div>
          {[
            { label: 'New Orders', value: ordersByStatus?.pending, pct: ordersByStatus?.pendingPct, color: 'var(--yellow)' },
            { label: 'Ready', value: ordersByStatus?.processing, pct: ordersByStatus?.processingPct, color: '#f97316' },
            { label: 'Out for Delivery', value: ordersByStatus?.shipped, pct: ordersByStatus?.shippedPct, color: 'var(--blue)' },
            { label: 'Delivered', value: ordersByStatus?.delivered, pct: ordersByStatus?.deliveredPct, color: 'var(--green)' },
          ].map(item => (
            <div key={item.label} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{item.label}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{item.value || 0}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${item.pct || 0}%`, background: item.color }} />
              </div>
            </div>
          ))}
          <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--bg-hover)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Cancelled</span>
            <span style={{ color: 'var(--red)', fontWeight: 700 }}>{ordersByStatus?.cancelled || 0}</span>
          </div>
        </div>
      </div>

      <div className="dashboard-bottom">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Orders</span>
            <a href="/orders" style={{ fontSize: 12, color: 'var(--primary-dark)', textDecoration: 'none' }}>View all →</a>
          </div>

          <div style={{
            display: 'flex', gap: 6, marginBottom: 14,
            overflowX: 'auto', paddingBottom: 4,
            scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
          }}>
            <style>{`.filter-scroll::-webkit-scrollbar{display:none}`}</style>
            {STATUS_OPTS.map(s => (
              <button
                key={s}
                className={`chip ${statusFilter === s ? 'active' : ''}`}
                onClick={() => setStatusFilter(s)}
                style={{ fontSize: 11, padding: '4px 12px', flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {s.replaceAll('_', ' ')}
              </button>
            ))}
          </div>

          {isMobile ? (
            <div>
              {ordersLoading && (
                <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: 'auto' }} /></div>
              )}
              {!ordersLoading && recentOrders.length === 0 && (
                <div className="empty-state"><p>No orders found</p></div>
              )}
              {!ordersLoading && recentOrders.length > 0 && (
                recentOrders.map(order => (
                  <MobileOrderCard key={order.orderId} order={order} onClick={setSelectedOrderId} />
                ))
              )}
            </div>
          ) : (
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 700, width: '100%' }}>
                <thead>
                  <tr>
                    <th>Order ID</th><th>Customer</th><th>Seller</th>
                    <th>Products</th><th>Amount</th><th>Status</th><th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersLoading && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: 'auto' }} /></td></tr>
                  )}
                  {!ordersLoading && recentOrders.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No orders found</td></tr>
                  )}
                  {!ordersLoading && recentOrders.map(order => (
                    <tr key={order.orderId}
                      onClick={() => setSelectedOrderId(order.orderId)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedOrderId(order.orderId)}
                      tabIndex={0}
                      style={{ cursor: 'pointer', transition: 'background 0.15s', background: order.status === 'READY' ? '#fff7ed' : '' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = order.status === 'READY' ? '#fff7ed' : ''}>
                      <td>
                        <span style={{ color: 'var(--primary-dark)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                          {maskOrderId(order.displayId || order.orderId)}
                        </span>
                        {order.status === 'READY' && (
                          <span style={{ marginLeft: 6, fontSize: 10, background: '#f97316', color: '#fff', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>READY</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{order.customerName || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{order.sellerName || '—'}</td>
                      <td style={{ maxWidth: 160 }}>
                        {order.productNames
                          ? <span title={order.productNames} style={{ display: 'block', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{order.productNames}</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ color: 'var(--green)', fontWeight: 700 }}>₹{Number(order.amount).toLocaleString('en-IN')}</td>
                      <td><span className={`badge ${getBadgeClass(order.status)}`}>{order.status?.replaceAll('_', ' ') || '—'}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="pagination" style={{ marginTop: 14 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {recentOrders.length} of {ordersTotal} orders
            </span>
            <div className="pagination-controls">
              <button className="btn btn-ghost btn-sm" disabled={ordersPage === 0} onClick={() => setOrdersPage(p => p - 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <ChevronLeft size={14} /> Prev
              </button>
              <span style={{ padding: '6px 12px', fontSize: 13 }}>
                {ordersPage + 1} / {Math.max(1, ordersTotalPages)}
              </span>
              <button className="btn btn-ghost btn-sm" disabled={ordersPage >= ordersTotalPages - 1} onClick={() => setOrdersPage(p => p + 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Top Sellers */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Top Sellers</span>
            <a href="/sellers" style={{ fontSize: 12, color: 'var(--primary-dark)', textDecoration: 'none' }}>View all →</a>
          </div>
          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[...(data?.topSellers || [])]
                .sort((a, b) => (b.totalOrders || 0) - (a.totalOrders || 0))
                .map((seller, i) => (
                  <div key={seller.sellerId} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', background: 'var(--bg-hover)', borderRadius: 10
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: 6, background: 'var(--primary-glow-sm)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, color: 'var(--primary-dark)'
                    }}>{i + 1}</div>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: '#28a428',
                      color: '#fff',
                      fontSize: 15, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, overflow: 'hidden',
                      border: '2px solid #1e7a1e',
                    }}>
                      {seller.profilePicture
                        ? <img src={seller.profilePicture} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : seller.businessName?.[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {seller.businessName}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{seller.totalOrders} orders</div>
                    </div>
                  </div>
                ))}
              {(data?.topSellers || []).length === 0 && (
                <div className="empty-state"><p>No seller data yet</p></div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedOrderId && (
        <OrderDetailModal orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />
      )}
    </Layout>
  );
}